import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import RefundRequestPanel from '../components/RefundRequestPanel';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, User, Users, Wrench, Activity, CreditCard, Edit, Trash2, Plus, Award, Lock,
  ShieldCheck, ArrowUpRight, GraduationCap, Linkedin, Image as ImageIcon, Copy, Link2, RefreshCw,
  Twitter, Facebook, MessageCircle, Mail, QrCode, Download, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import DirectMessagesWidget from '../components/DirectMessagesWidget';
import ProfileCompletionBanner from '../components/ProfileCompletionBanner';
import UserEngagementBanner from '../components/UserEngagementBanner';
import ThemeToggle from '../components/ThemeToggle';
import ToolSelector from '../components/ToolSelector';
import ActivityFeed from '../components/ActivityFeed';
import BrandMark from '../components/BrandMark';
import ReferralMilestoneCelebration from '../components/ReferralMilestoneCelebration';

const UserPortal = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [profile, setProfile] = useState({});
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({});

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [equipment, setEquipment] = useState([]);
  const [showEquipmentDialog, setShowEquipmentDialog] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [equipmentForm, setEquipmentForm] = useState({
    brand: '', model_size: '', purchase_date: '', primary_use: '', ownership_status: '', notes: '', manufacturer_brand: '',
  });

  const [stats, setStats] = useState({});
  const [badges, setBadges] = useState({});
  const [options, setOptions] = useState({});
  const [certs, setCerts] = useState([]);
  const [wallPref, setWallPref] = useState({ opt_out: false, total_referrals: 0, per_cert: [], ambassador: false, pro_until: null });
  const [refInfo, setRefInfo] = useState(null);
  const [myRank, setMyRank] = useState(null); // { rank, total_referrals, leaderboard_size }
  const [topAmbassadors, setTopAmbassadors] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [rk, lb] = await Promise.all([
          axios.get(`${API}/referrals/my-rank`).then((r) => r.data).catch(() => null),
          axios.get(`${API}/referrals/leaderboard?limit=5`).then((r) => r.data?.entries || []).catch(() => []),
        ]);
        if (alive) { setMyRank(rk); setTopAmbassadors(lb); }
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [API]);
  // QR download for the user's referral link. Loaded lazily on first
  // "Show QR" click so the initial portal render stays snappy.
  const [refQrUrl, setRefQrUrl] = useState(null);
  const [refQrLoading, setRefQrLoading] = useState(false);
  const loadRefQr = async () => {
    if (refQrUrl || !refInfo?.code) return;
    setRefQrLoading(true);
    try {
      const tk = window.localStorage.getItem('token');
      const res = await axios.get(`${API}/referrals/me/qr?size=512`, {
        responseType: 'blob',
        headers: tk ? { Authorization: `Bearer ${tk}` } : {},
      });
      setRefQrUrl(URL.createObjectURL(res.data));
    } catch { toast.error('Could not load QR'); }
    finally { setRefQrLoading(false); }
  };
  const downloadRefQr = async () => {
    await loadRefQr();
    // After state settles, chain a download click. If loadRefQr just fired we
    // still have `refQrUrl` in the next tick — a tiny setTimeout lets React
    // flush the blob URL into state before we synth-click.
    setTimeout(() => {
      const url = refQrUrl || (document.querySelector('#ref-qr-img')?.src);
      if (!url) { toast.error('QR not ready yet — tap again'); return; }
      const a = document.createElement('a');
      a.href = url;
      a.download = `referral-${refInfo?.code || 'code'}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }, 150);
  };
  const shareToNetwork = (network) => {
    const url = encodeURIComponent(refInfo?.share_url || '');
    const text = encodeURIComponent(
      "I'm learning freeze-drying at Freeze Dry Academy — join me and we both level up. My invite link:",
    );
    let target = '';
    if (network === 'x')        target = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
    if (network === 'linkedin') target = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    if (network === 'facebook') target = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    if (network === 'whatsapp') target = `https://api.whatsapp.com/send?text=${text}%20${url}`;
    if (network === 'email')    target = `mailto:?subject=${encodeURIComponent('Join me on Freeze Dry Academy')}&body=${text}%20${url}`;
    if (target) window.open(target, '_blank', 'noopener,noreferrer');
  };

  const [celebration, setCelebration] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchEquipment();
    fetchStats();
    fetchBadges();
    fetchOptions();
    fetchCertificates();
    fetchWallPref();
    fetchReferral();
    fetchCelebration();
  }, []);

  const fetchProfile = async () => {
    try {
      const r = await axios.get(`${API}/user/profile`);
      setProfile(r.data); setProfileForm(r.data);
    } catch { toast.error('Failed to load profile'); }
  };
  const fetchEquipment = async () => {
    try { const r = await axios.get(`${API}/user/equipment`); setEquipment(r.data); } catch { /* noop */ }
  };
  const fetchStats = async () => {
    try { const r = await axios.get(`${API}/user/stats`); setStats(r.data); } catch { /* noop */ }
  };
  const fetchBadges = async () => {
    try { const r = await axios.get(`${API}/user/badges`); setBadges(r.data); } catch { /* noop */ }
  };
  const fetchCertificates = async () => {
    try {
      const r = await axios.get(`${API}/lms/my-certificates`);
      setCerts(r.data?.certificates || []);
    } catch { /* noop */ }
  };
  const fetchWallPref = async () => {
    try {
      const r = await axios.get(`${API}/user/graduate-wall-preference`);
      setWallPref(r.data || { opt_out: false, total_referrals: 0, per_cert: [], ambassador: false, pro_until: null });
    } catch { /* noop */ }
  };
  const fetchReferral = async () => {
    try {
      const r = await axios.get(`${API}/referrals/me`);
      setRefInfo(r.data || null);
    } catch { /* noop */ }
  };
  const fetchCelebration = async () => {
    try {
      const r = await axios.get(`${API}/referrals/celebration`);
      if (r.data?.pending) setCelebration(r.data);
    } catch { /* noop */ }
  };
  const dismissCelebration = async () => {
    setCelebration(null);
    try { await axios.post(`${API}/referrals/celebration/dismiss`); }
    catch { /* noop */ }
    // Refresh referral summary in case pro_until changed after grant.
    fetchReferral();
  };
  const copyRefLink = async () => {
    if (!refInfo?.share_url) return;
    try { await navigator.clipboard.writeText(refInfo.share_url); toast.success('Referral link copied'); }
    catch { toast.error('Copy failed'); }
  };
  const copyRefCode = async () => {
    if (!refInfo?.code) return;
    try { await navigator.clipboard.writeText(refInfo.code); toast.success(`Copied ${refInfo.code}`); }
    catch { toast.error('Copy failed'); }
  };
  const regenerateRef = async () => {
    if (!window.confirm('Rotate your referral code? The old code stops working immediately.')) return;
    try {
      const r = await axios.post(`${API}/referrals/me/regenerate`, { confirm: true });
      setRefInfo(r.data);
      toast.success('New referral code generated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not regenerate');
    }
  };
  const toggleWallOptOut = async () => {
    const next = !wallPref.opt_out;
    try {
      await axios.put(`${API}/user/graduate-wall-preference`, { opt_out: next });
      setWallPref((p) => ({ ...p, opt_out: next }));
      toast.success(next ? 'Hidden from the Graduate Wall' : 'Visible on the Graduate Wall');
    } catch {
      toast.error('Could not update preference');
    }
  };
  const fetchOptions = async () => {
    try {
      const r = await axios.get(`${API}/gamification/options`);
      setOptions(r.data || {});
    } catch {
      setOptions({
        countries: [], us_states: [], canadian_provinces: [], experience_levels: [],
        how_heard_options: [], main_interests: [], brands: [], model_sizes: [], primary_uses: [],
      });
    }
  };

  const handleProfileUpdate = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/user/profile`, {
        first_name: profileForm.first_name,
        last_name: profileForm.last_name,
        avatar_url: profileForm.avatar_url,
        city: profileForm.city,
        state: profileForm.state,
        country: profileForm.country,
        postal_code: profileForm.postal_code,
      });
      toast.success('Profile updated');
      setProfileEditing(false);
      fetchProfile();
    } catch { toast.error('Failed to update profile'); }
    finally { setLoading(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match'); return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters'); return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/user/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success('Password changed');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setLoading(false); }
  };

  const handleEquipmentSubmit = async () => {
    setLoading(true);
    try {
      if (editingEquipment) {
        await axios.put(`${API}/user/equipment/${editingEquipment.id}`, equipmentForm);
        toast.success('Equipment updated');
      } else {
        await axios.post(`${API}/user/equipment`, equipmentForm);
        toast.success('Equipment added');
      }
      setShowEquipmentDialog(false);
      setEditingEquipment(null);
      setEquipmentForm({ brand: '', model_size: '', purchase_date: '', primary_use: '', ownership_status: '', notes: '', manufacturer_brand: '' });
      fetchEquipment();
    } catch { toast.error('Failed to save equipment'); }
    finally { setLoading(false); }
  };

  const handleDeleteEquipment = async (id) => {
    if (!window.confirm('Delete this equipment?')) return;
    try {
      await axios.delete(`${API}/user/equipment/${id}`);
      toast.success('Equipment deleted');
      fetchEquipment();
    } catch { toast.error('Failed to delete'); }
  };

  const firstName = profile.first_name || user?.email?.split('@')[0] || 'friend';

  return (
    <div className="min-h-screen bg-background" data-testid="user-portal-page">
      <ReferralMilestoneCelebration
        celebration={celebration}
        shareLink={refInfo?.share_url}
        onClose={dismissCelebration}
        API={API}
      />
      {/* Unified top nav */}
      <ToolSelector />

      {/* Nudge users to finish the demographic + machine survey — this
          is the data source for the Analytics dashboard, so a low
          completion rate hurts every downstream insight. */}
      <ProfileCompletionBanner />
      <UserEngagementBanner />

      {/* Greeting hero */}
      <section className="container mx-auto px-6 pt-6">
        <div className="bg-navy-spotlight text-background rounded-3xl p-8 sm:p-10 relative overflow-hidden">
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="h-16 w-16 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center font-heading text-2xl font-extrabold flex-shrink-0">
                {(profile.first_name?.[0] || user?.email?.[0] || '?').toUpperCase()}
              </div>
              <div>
                <span className="pill-badge"><ShieldCheck className="w-3.5 h-3.5" /> Account</span>
                <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">
                  {firstName}<span className="text-amber-brand">.</span>
                </h1>
                <p className="mt-1 text-background/70 text-sm">{profile.email}</p>
                <p className="mt-2 text-background/60 text-xs">
                  Edits here update your <button
                    type="button"
                    onClick={() => navigate(`/u/${user?.handle || user?.id}`)}
                    className="underline hover:text-background font-semibold"
                    data-testid="portal-view-community-profile"
                  >public community profile</button> automatically — no double entry.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 sm:gap-5 min-w-[280px]">
              <div className="rounded-xl bg-background/5 border border-background/10 px-3 py-3 text-center backdrop-blur-sm" data-testid="portal-stat-tier">
                <div className="text-[10px] uppercase tracking-widest text-background/60">Tier</div>
                <div className="mt-1 font-heading font-extrabold text-lg text-primary">{(user?.role || 'free').toUpperCase()}</div>
              </div>
              <div className="rounded-xl bg-background/5 border border-background/10 px-3 py-3 text-center backdrop-blur-sm" data-testid="portal-stat-days">
                <div className="text-[10px] uppercase tracking-widest text-background/60">Days here</div>
                <div className="mt-1 font-heading font-extrabold text-lg text-background">
                  {(() => {
                    const created = user?.created_at || profile.created_at;
                    if (!created) return '—';
                    const days = Math.max(0, Math.floor((Date.now() - new Date(created).getTime()) / 86400000));
                    return days;
                  })()}
                </div>
              </div>
              <div className="rounded-xl bg-background/5 border border-background/10 px-3 py-3 text-center backdrop-blur-sm" data-testid="portal-stat-badges">
                <div className="text-[10px] uppercase tracking-widest text-background/60">Badges</div>
                <div className="mt-1 font-heading font-extrabold text-lg text-background">
                  {badges?.badges?.length ?? badges?.total ?? 0}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <main className="container mx-auto px-6 py-10">
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-6 bg-muted/70" data-testid="portal-tabs">
            <TabsTrigger value="profile" data-testid="tab-profile"><User className="w-4 h-4 mr-2" />Profile</TabsTrigger>
            <TabsTrigger value="equipment" data-testid="tab-equipment"><Wrench className="w-4 h-4 mr-2" />Equipment</TabsTrigger>
            <TabsTrigger value="certificates" data-testid="tab-certificates"><GraduationCap className="w-4 h-4 mr-2" />Certificates</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity"><Activity className="w-4 h-4 mr-2" />Activity</TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing"><CreditCard className="w-4 h-4 mr-2" />Billing</TabsTrigger>
          </TabsList>

          {/* PROFILE */}
          <TabsContent value="profile">
            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="pro-card lg:col-span-2 border-0">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="font-heading text-xl font-bold">Profile</CardTitle>
                      <CardDescription>Your personal details.</CardDescription>
                    </div>
                    <Button
                      variant={profileEditing ? 'default' : 'outline'}
                      size="sm"
                      className="rounded-full font-semibold"
                      onClick={() => (profileEditing ? handleProfileUpdate() : setProfileEditing(true))}
                      disabled={loading}
                      data-testid="edit-profile-btn"
                    >
                      <Edit className="w-3.5 h-3.5 mr-1.5" />
                      {profileEditing ? 'Save changes' : 'Edit profile'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-5">
                  {profileEditing && (
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">Avatar URL</Label>
                      <Input
                        value={profileForm.avatar_url || ''}
                        onChange={(e) => setProfileForm({ ...profileForm, avatar_url: e.target.value })}
                        placeholder="https://example.com/avatar.jpg"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">First name</Label>
                      <Input value={profileForm.first_name || ''} onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })} disabled={!profileEditing} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">Last name</Label>
                      <Input value={profileForm.last_name || ''} onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })} disabled={!profileEditing} />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                    <Input value={profile.email || ''} disabled />
                    <p className="text-xs text-muted-foreground">Email can&apos;t be changed after signup.</p>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Country</Label>
                    {profileEditing ? (
                      <Select value={profileForm.country || ''} onValueChange={(v) => setProfileForm({ ...profileForm, country: v, state: '' })}>
                        <SelectTrigger><SelectValue placeholder="Select country…" /></SelectTrigger>
                        <SelectContent>
                          {options.countries?.length > 0
                            ? options.countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)
                            : <SelectItem value="loading" disabled>Loading…</SelectItem>}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input value={profileForm.country || ''} disabled />
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">State / Province</Label>
                      <Input value={profileForm.state || ''} onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })} disabled={!profileEditing} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">City</Label>
                      <Input value={profileForm.city || ''} onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })} disabled={!profileEditing} />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      {profileForm.country === 'Canada' ? 'Postal code' : 'ZIP code'}
                    </Label>
                    <Input
                      value={profileForm.postal_code || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, postal_code: e.target.value })}
                      placeholder={profileForm.country === 'Canada' ? 'A1A 1A1' : '90210'}
                      disabled={!profileEditing}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Right rail */}
              <div className="space-y-6">
                <ProfileCompletionCard API={API} data-testid="profile-completion-card" />

                <DirectMessagesWidget compact />

                <Card className="pro-card border-0">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg font-bold">Account summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Row label="Member since" value={profile.created_at ? new Date(profile.created_at).toLocaleDateString() : '—'} />
                    <Row label="Subscription" value={<Badge className="bg-primary/10 text-primary hover:bg-primary/10">{(profile.role || 'free').toUpperCase()}</Badge>} />
                    <Row label="Status" value={<Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/10">Active</Badge>} />
                  </CardContent>
                </Card>

                <Card className="pro-card border-0" data-testid="study-buddy-prefs-card">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg font-bold flex items-center gap-2">
                      <Users className="w-4 h-4" /> Study Buddy
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Get matched with a fellow learner working on the same lesson.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <StudyBuddyOptInToggle API={API} />
                  </CardContent>
                </Card>

                <Card className="pro-card border-0">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg font-bold flex items-center gap-2">
                      <Lock className="w-4 h-4" /> Change password
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handlePasswordChange} className="space-y-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Current password</Label>
                        <Input type="password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} required data-testid="current-password-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">New password</Label>
                        <Input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} required data-testid="new-password-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Confirm new password</Label>
                        <Input type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} required data-testid="confirm-password-input" />
                      </div>
                      <Button type="submit" disabled={loading} className="w-full rounded-full font-semibold" data-testid="update-password-btn">
                        Update password
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* EQUIPMENT */}
          <TabsContent value="equipment">
            <Card className="pro-card border-0">
              <CardHeader className="flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle className="font-heading text-xl font-bold">My freeze dryers</CardTitle>
                  <CardDescription>Machines you own, use or plan to buy.</CardDescription>
                </div>
                <Dialog open={showEquipmentDialog} onOpenChange={setShowEquipmentDialog}>
                  <DialogTrigger asChild>
                    <Button
                      className="rounded-full font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
                      onClick={() => {
                        setEditingEquipment(null);
                        setEquipmentForm({ brand: '', model_size: '', purchase_date: '', primary_use: '', ownership_status: '', notes: '', manufacturer_brand: '' });
                      }}
                      data-testid="add-equipment-btn"
                    >
                      <Plus className="w-4 h-4 mr-1.5" /> Add machine
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-heading">{editingEquipment ? 'Edit' : 'Add'} freeze dryer</DialogTitle>
                      <DialogDescription>Enter your equipment details.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-3">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Ownership</Label>
                        <Select value={equipmentForm.ownership_status || undefined} onValueChange={(v) => setEquipmentForm({ ...equipmentForm, ownership_status: v })}>
                          <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Own">Own</SelectItem>
                            <SelectItem value="Ordered">Ordered</SelectItem>
                            <SelectItem value="Planning to Buy">Planning to buy</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {[
                        { key: 'brand', label: 'Brand', src: options.brands },
                        { key: 'model_size', label: 'Model / size', src: options.model_sizes },
                        { key: 'primary_use', label: 'Primary use', src: options.primary_uses },
                      ].map(f => (
                        <div key={f.key} className="space-y-2">
                          <Label className="text-xs uppercase tracking-wider text-muted-foreground">{f.label}</Label>
                          <RadioGroup value={equipmentForm[f.key] || ''} onValueChange={(v) => setEquipmentForm({ ...equipmentForm, [f.key]: v })}>
                            {f.src?.length > 0
                              ? f.src.map(v => (
                                <div key={v} className="flex items-center gap-2">
                                  <RadioGroupItem value={v} id={`${f.key}-${v}`} />
                                  <Label htmlFor={`${f.key}-${v}`} className="font-normal cursor-pointer">{v}</Label>
                                </div>
                              ))
                              : <p className="text-xs text-muted-foreground">Loading…</p>}
                          </RadioGroup>
                        </div>
                      ))}
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Purchase date</Label>
                        <Input type="date" value={equipmentForm.purchase_date} onChange={(e) => setEquipmentForm({ ...equipmentForm, purchase_date: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                          Manufacturer brand <span className="normal-case text-[10px] text-muted-foreground/80">(private — internal tracking only)</span>
                        </Label>
                        <Input
                          list="fda-manufacturer-brands"
                          value={equipmentForm.manufacturer_brand || ''}
                          onChange={(e) => setEquipmentForm({ ...equipmentForm, manufacturer_brand: e.target.value })}
                          placeholder="e.g. Harvest Right, HomeLyph, LyphLine…"
                          data-testid="manufacturer-brand-input"
                        />
                        <datalist id="fda-manufacturer-brands">
                          {(options.manufacturer_brand_suggestions || []).map((b) => (
                            <option key={b} value={b} />
                          ))}
                        </datalist>
                        <p className="text-[11px] text-muted-foreground">
                          Used only for our internal machine-tracking analytics — never shown in public
                          recommendations, community posts, or your public profile.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
                        <Input value={equipmentForm.notes} onChange={(e) => setEquipmentForm({ ...equipmentForm, notes: e.target.value })} placeholder="Additional details…" />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" className="rounded-full font-semibold" onClick={() => setShowEquipmentDialog(false)}>Cancel</Button>
                      <Button onClick={handleEquipmentSubmit} disabled={loading} className="rounded-full font-semibold" data-testid="save-equipment-btn">Save</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {equipment.length === 0 ? (
                  <div className="text-center py-14 text-muted-foreground">
                    <Wrench className="w-12 h-12 mx-auto mb-4 opacity-40" />
                    <p className="font-heading text-lg font-bold text-foreground">No machines yet</p>
                    <p className="text-sm mt-1">Add your first freeze dryer to personalize your calculators.</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {equipment.map((item) => (
                      <div key={item.id} className="pro-card p-5" data-testid={`equipment-item-${item.id}`}>
                        <div className="flex justify-between items-start gap-3">
                          <div className="min-w-0 flex-1">
                            <h3 className="font-heading font-bold text-base truncate">{item.brand} — {item.model_size}</h3>
                            <p className="text-xs text-muted-foreground mt-0.5">{item.primary_use}</p>
                            <div className="flex flex-wrap gap-1.5 mt-3">
                              <Badge className="bg-primary/10 text-primary hover:bg-primary/10">{item.ownership_status}</Badge>
                              {item.purchase_date && <Badge variant="outline">{new Date(item.purchase_date).toLocaleDateString()}</Badge>}
                            </div>
                            {item.notes && <p className="text-xs mt-3 text-muted-foreground">{item.notes}</p>}
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => { setEditingEquipment(item); setEquipmentForm(item); setShowEquipmentDialog(true); }}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteEquipment(item.id)} className="text-destructive hover:bg-destructive/10">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* CERTIFICATES */}
          <TabsContent value="certificates">
            <div className="grid gap-4 lg:grid-cols-3" data-testid="certificates-tab-content">
              {/* Left: privacy + referral rail */}
              <Card className="pro-card border-0 lg:col-span-1">
                <CardHeader>
                  <CardTitle className="font-heading text-base font-bold flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-primary" /> Wall privacy & referrals
                  </CardTitle>
                  <CardDescription>Control how you show up publicly and see who signed up through you.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Opt-out toggle */}
                  <div className="rounded-2xl border border-border p-4 flex items-start gap-3" data-testid="wall-optout-card">
                    <div className="mt-0.5 flex-shrink-0">
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${wallPref.opt_out ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'}`}>
                        <GraduationCap className="w-4 h-4" />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-foreground">
                        {wallPref.opt_out ? 'Hidden from Graduate Wall' : 'Visible on Graduate Wall'}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        {wallPref.opt_out
                          ? 'Your certificates stay private. Public verify links still work for people you share them with, but you won\'t appear on /graduates.'
                          : 'Your first name + last initial appear alongside your credentials on our public Graduate Wall. Great for social proof and referrals.'}
                      </p>
                      <button
                        type="button"
                        onClick={toggleWallOptOut}
                        role="switch"
                        aria-checked={!wallPref.opt_out}
                        data-testid="wall-optout-toggle"
                        className={`mt-3 inline-flex items-center gap-2 h-9 px-4 rounded-full text-sm font-bold transition-colors
                          ${wallPref.opt_out ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'border border-border text-foreground/80 hover:bg-muted'}`}
                      >
                        {wallPref.opt_out ? 'Show me on the wall' : 'Hide me from the wall'}
                      </button>
                    </div>
                  </div>

                  {/* Referral stats */}
                  <div className="rounded-2xl border border-border p-4" data-testid="wall-referrals-card">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-amber-500/15 text-amber-700 flex items-center justify-center">
                        <Award className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-foreground">My referral link</div>
                        <div className="text-xs text-muted-foreground">
                          Every 5 signups adds another month of Pro to your account.
                        </div>
                      </div>
                    </div>

                    {refInfo?.code && (
                      <div className="mt-3 rounded-xl border border-border bg-muted/40 p-3">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Your code</div>
                        <div className="mt-1 flex items-center gap-2">
                          <code className="font-mono text-sm font-bold text-foreground" data-testid="my-referral-code">{refInfo.code}</code>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={copyRefCode}
                            className="h-7 px-2"
                            title="Copy code"
                            data-testid="copy-referral-code"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <div className="mt-2 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Share link</div>
                        <div className="mt-1 flex items-center gap-2">
                          <input
                            readOnly
                            value={refInfo.share_url || ''}
                            onFocus={(e) => e.target.select()}
                            className="flex-1 min-w-0 px-2 py-1 rounded-md bg-background border border-border text-xs font-mono"
                            data-testid="my-referral-link"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={copyRefLink}
                            className="h-7 rounded-full px-3"
                            data-testid="copy-referral-link"
                          >
                            <Link2 className="w-3.5 h-3.5 mr-1" /> Copy
                          </Button>
                        </div>
                        <button
                          type="button"
                          onClick={regenerateRef}
                          className="mt-2 text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                          data-testid="regenerate-referral-code"
                        >
                          <RefreshCw className="w-3 h-3" /> Rotate code
                        </button>
                        {typeof refInfo.credits_from_referrals === 'number' && refInfo.credits_from_referrals > 0 && (
                          <div
                            className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-[11px] font-bold text-amber-800 dark:text-amber-300"
                            data-testid="referral-credits-earned"
                            title="AI credits earned from every referral signup + milestone bonuses"
                          >
                            +{refInfo.credits_from_referrals.toLocaleString()} AI credits banked from referrals
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mt-3 flex items-baseline gap-2" data-testid="wall-referrals-count">
                      <span className="font-heading text-3xl font-extrabold text-foreground">
                        {refInfo?.total_referrals ?? wallPref.total_referrals}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        / {refInfo?.next_milestone_at ?? 5} to unlock a Pro month
                      </span>
                    </div>
                    {refInfo && (
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        Certificate: {refInfo.cert_referrals} · Direct link: {refInfo.direct_referrals}
                        {refInfo.pro_months_earned > 0 && <> · <span className="font-bold text-emerald-700">{refInfo.pro_months_earned} Pro month{refInfo.pro_months_earned === 1 ? '' : 's'} earned</span></>}
                      </div>
                    )}
                    <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{
                          width: `${(() => {
                            const total = refInfo?.total_referrals ?? wallPref.total_referrals ?? 0;
                            const next = refInfo?.next_milestone_at ?? 5;
                            const prev = Math.max(0, next - 5);
                            const pct = ((total - prev) / (next - prev)) * 100;
                            return Math.min(100, Math.max(0, pct));
                          })()}%`,
                        }}
                      />
                    </div>

                    {/* Credit-milestone goalpost — separate from the Pro-months
                        bar above. Fires the 5/10/25 tiered credit bonus. */}
                    {refInfo?.next_credit_milestone && (
                      <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid="referral-credit-milestone">
                        <div className="flex items-center justify-between text-[11px] font-bold text-amber-800 dark:text-amber-300">
                          <span className="uppercase tracking-widest">Next credit reward</span>
                          <span>
                            {refInfo.next_credit_milestone.remaining === 0
                              ? 'Ready to unlock'
                              : `${refInfo.next_credit_milestone.remaining} to go`}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-foreground/85">
                          <span className="font-bold">
                            {(refInfo.total_referrals ?? 0)} of {refInfo.next_credit_milestone.threshold}
                          </span>{' '}
                          signups · unlocks{' '}
                          <span className="font-bold text-amber-700 dark:text-amber-300">
                            +{refInfo.next_credit_milestone.credits.toLocaleString()} AI credits
                          </span>
                        </div>
                        <div className="mt-2 h-1.5 rounded-full bg-amber-500/15 overflow-hidden">
                          <div
                            className="h-full bg-amber-500 transition-all"
                            style={{ width: `${refInfo.next_credit_milestone.progress_pct}%` }}
                            data-testid="referral-credit-milestone-bar"
                          />
                        </div>
                        {/* Full milestone ladder — shows unlocked / next / locked
                            tiers so members see the whole 5/10/25 goalpost. */}
                        <div className="mt-3 grid grid-cols-3 gap-2" data-testid="referral-milestone-strip">
                          {[
                            { threshold: 5,  credits: 50  },
                            { threshold: 10, credits: 150 },
                            { threshold: 25, credits: 500 },
                          ].map((tier) => {
                            const total = refInfo?.total_referrals ?? 0;
                            const done  = total >= tier.threshold;
                            const next  = !done && refInfo?.next_credit_milestone?.threshold === tier.threshold;
                            return (
                              <div
                                key={tier.threshold}
                                className={`rounded-lg border p-2 text-center ${
                                  done
                                    ? 'border-emerald-500/40 bg-emerald-500/10'
                                    : next
                                      ? 'border-amber-500/60 bg-amber-500/15 ring-1 ring-amber-500/40'
                                      : 'border-border bg-background/50'
                                }`}
                                data-testid={`milestone-tier-${tier.threshold}`}
                              >
                                <div className={`text-[9px] uppercase tracking-widest font-bold ${
                                  done ? 'text-emerald-700 dark:text-emerald-400' : next ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'
                                }`}>
                                  {done ? 'Unlocked' : next ? 'Next' : 'Locked'}
                                </div>
                                <div className="mt-0.5 font-heading text-lg font-extrabold text-foreground">
                                  {tier.threshold}
                                </div>
                                <div className="text-[10px] text-muted-foreground">signups</div>
                                <div className={`mt-1 text-[11px] font-bold ${
                                  done ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'
                                }`}>
                                  +{tier.credits} cr
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Share Kit — social buttons + QR download so referrers
                        can post their link without hunting for it. */}
                    {refInfo?.share_url && (
                      <div className="mt-3 rounded-xl border border-border bg-background/50 p-3" data-testid="referral-share-kit">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Share kit</div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => shareToNetwork('x')}
                            className="h-8 rounded-full px-3 text-xs"
                            data-testid="share-x-btn"
                            title="Share on X"
                          >
                            <Twitter className="w-3.5 h-3.5 mr-1" /> X
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => shareToNetwork('linkedin')}
                            className="h-8 rounded-full px-3 text-xs"
                            data-testid="share-linkedin-btn"
                            title="Share on LinkedIn"
                          >
                            <Linkedin className="w-3.5 h-3.5 mr-1" /> LinkedIn
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => shareToNetwork('facebook')}
                            className="h-8 rounded-full px-3 text-xs"
                            data-testid="share-facebook-btn"
                            title="Share on Facebook"
                          >
                            <Facebook className="w-3.5 h-3.5 mr-1" /> Facebook
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => shareToNetwork('whatsapp')}
                            className="h-8 rounded-full px-3 text-xs"
                            data-testid="share-whatsapp-btn"
                            title="Share on WhatsApp"
                          >
                            <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => shareToNetwork('email')}
                            className="h-8 rounded-full px-3 text-xs"
                            data-testid="share-email-btn"
                            title="Share via email"
                          >
                            <Mail className="w-3.5 h-3.5 mr-1" /> Email
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={loadRefQr}
                            className="h-8 rounded-full px-3 text-xs"
                            data-testid="show-qr-btn"
                          >
                            <QrCode className="w-3.5 h-3.5 mr-1" /> {refQrUrl ? 'Hide QR' : 'Show QR'}
                          </Button>
                        </div>
                        {refQrLoading && !refQrUrl && (
                          <div className="mt-3 text-xs text-muted-foreground">Loading QR…</div>
                        )}
                        {refQrUrl && (
                          <div className="mt-3 flex flex-col items-center gap-2" data-testid="referral-qr-block">
                            <img
                              id="ref-qr-img"
                              src={refQrUrl}
                              alt={`QR for ${refInfo.code}`}
                              className="w-40 h-40 rounded-lg border border-border"
                            />
                            <Button
                              size="sm"
                              onClick={downloadRefQr}
                              className="h-8 rounded-full font-bold px-4 text-xs"
                              data-testid="download-qr-btn"
                            >
                              <Download className="w-3.5 h-3.5 mr-1" /> Download PNG
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Leaderboard rank strip — "You're ranked #N of M" so
                        members see how they stack up against everyone else. */}
                    {myRank?.rank && (
                      <div className="mt-3 rounded-xl border border-primary/30 bg-primary/5 p-3" data-testid="portal-my-rank">
                        <div className="flex items-center justify-between">
                          <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Ambassador leaderboard</div>
                            <div className="mt-1 font-heading text-base font-extrabold">
                              You're ranked <span className="text-primary">#{myRank.rank}</span>{' '}
                              <span className="text-muted-foreground font-normal">of {myRank.leaderboard_size}</span>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-heading text-2xl font-black text-foreground leading-none">{myRank.total_referrals}</div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">signups</div>
                          </div>
                        </div>
                        {topAmbassadors.length > 0 && (
                          <ol className="mt-3 space-y-1" data-testid="portal-top-ambassadors">
                            {topAmbassadors.slice(0, 3).map((a) => (
                              <li key={a.rank} className="flex items-center justify-between text-[11px]">
                                <span className="flex items-center gap-2 min-w-0">
                                  <span className="w-5 h-5 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-[9px] shrink-0">
                                    {a.rank}
                                  </span>
                                  <span className="truncate">{a.display_name}</span>
                                  {a.milestone_tier && (
                                    <span className="ml-1 text-[9px] uppercase tracking-widest font-bold text-amber-700 dark:text-amber-400">
                                      {a.milestone_tier.key}
                                    </span>
                                  )}
                                </span>
                                <span className="font-bold text-foreground shrink-0">{a.total_referrals}</span>
                              </li>
                            ))}
                          </ol>
                        )}
                      </div>
                    )}

                    {(refInfo?.ambassador ?? wallPref.ambassador) ? (
                      <div className="mt-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3" data-testid="ambassador-reward-active">
                        <p className="text-xs font-bold text-emerald-700 flex items-center gap-1">
                          <ShieldCheck className="w-3.5 h-3.5" /> Ambassador badge unlocked
                        </p>
                        {(refInfo?.pro_until ?? wallPref.pro_until) && (
                          <p className="mt-1 text-xs text-emerald-700/90">
                            Pro access active through <span className="font-bold">{new Date(refInfo?.pro_until ?? wallPref.pro_until).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </p>
                        )}
                        <p className="mt-1 text-[11px] text-emerald-700/80">
                          Every additional 5 referrals extends your Pro access by another month.
                        </p>
                        {(() => {
                          const topCert = (wallPref.per_cert || []).slice().sort((a, b) => (b.referrals || 0) - (a.referrals || 0))[0];
                          if (!topCert?.cert_number) return null;
                          const verify = `${window.location.origin}/certificate/${topCert.cert_number}?ref=${topCert.cert_number}`;
                          const share = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(verify)}`;
                          return (
                            <a
                              href={share}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-3 inline-flex items-center gap-1.5 rounded-full font-bold px-4 h-9 text-xs bg-[#0A66C2] text-white hover:bg-[#0a5aad] transition-colors"
                              data-testid="ambassador-share-win"
                              title="Post a celebration to LinkedIn — the branded gold-seal card unfurls automatically and every signup extends your Pro month."
                            >
                              <Linkedin className="w-3.5 h-3.5" /> Share the win
                            </a>
                          );
                        })()}
                      </div>
                    ) : (
                      <p className="mt-3 text-xs text-muted-foreground">
                        Share your link or any certificate. When 5 people join, you unlock a free month of Pro + the Ambassador badge automatically.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Right: certificates list */}
              <Card className="pro-card border-0 lg:col-span-2">
              <CardHeader>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <CardTitle className="font-heading text-xl font-bold flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-primary" /> My certificates
                    </CardTitle>
                    <CardDescription>
                      Every credential you&apos;ve earned — signed, HMAC-verified, and shareable.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      onClick={() => navigate('/my-certificates')}
                      className="rounded-full font-bold"
                      data-testid="certificates-view-all"
                    >
                      Full page view <ArrowUpRight className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => navigate('/coursework-transcript')}
                      className="rounded-full font-bold"
                      data-testid="certificates-view-transcript"
                    >
                      Printable transcript
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {certs.length === 0 ? (
                  <div className="text-center py-12 rounded-2xl bg-muted/40 border border-dashed border-border" data-testid="certificates-empty">
                    <GraduationCap className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                    <h3 className="font-heading text-lg font-extrabold text-foreground">
                      Your trophy shelf is still empty.
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                      Complete any Learning course to earn your first certificate — with a unique verifiable ID and a share-ready gold-foil card.
                    </p>
                    <Button
                      onClick={() => navigate('/learning')}
                      className="mt-5 rounded-full font-bold bg-primary hover:bg-primary/90"
                      data-testid="certificates-browse-courses"
                    >
                      Browse courses
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="certificates-list">
                    {certs.map((c) => {
                      const verifyUrl = `${window.location.origin}/certificate/${c.cert_number}`;
                      const shareUrl = `${verifyUrl}?ref=${c.cert_number}`;
                      const kindLabel = c.kind === 'designation'
                        ? 'Professional Designation'
                        : c.kind === 'specialty' ? 'Specialty Certificate' : 'Certificate of Completion';
                      const title = c.designation_title || c.specialty_title || c.course_title;
                      const refs = wallPref.per_cert?.find((p) => p.cert_number === c.cert_number)?.referrals || 0;
                      return (
                        <div
                          key={c.cert_number}
                          className="pro-card p-4 flex flex-col gap-3 hover:border-primary/60 transition-colors"
                          data-testid={`portal-cert-${c.cert_number}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                              <GraduationCap className="w-5 h-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-primary">
                                {kindLabel}
                              </div>
                              <h3 className="font-heading text-base font-extrabold text-foreground tracking-tight truncate">
                                {title}
                              </h3>
                              <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground">
                                <span className="font-mono font-bold text-foreground/80">{c.cert_number}</span>
                                <span>·</span>
                                <span>{new Date(c.issued_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button
                              size="sm"
                              onClick={() => navigate(`/certificate/${c.cert_number}`)}
                              className="rounded-full font-bold bg-primary hover:bg-primary/90"
                              data-testid={`portal-cert-view-${c.cert_number}`}
                            >
                              View <ArrowUpRight className="w-3 h-3 ml-1" />
                            </Button>
                            <a
                              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center rounded-full font-bold px-3 h-8 text-xs bg-[#0A66C2] text-white hover:bg-[#0a5aad] transition-colors"
                              data-testid={`portal-cert-post-${c.cert_number}`}
                            >
                              <Linkedin className="w-3 h-3 mr-1" /> Post
                            </a>
                            <a
                              href={`${API}/verify/${c.cert_number}/share-card.png`}
                              target="_blank"
                              rel="noopener noreferrer"
                              download={`${c.cert_number}-share.png`}
                              className="inline-flex items-center rounded-full font-bold px-3 h-8 text-xs border border-border text-foreground/75 hover:bg-muted transition-colors"
                              data-testid={`portal-cert-share-image-${c.cert_number}`}
                            >
                              <ImageIcon className="w-3 h-3 mr-1" /> Image
                            </a>
                            {refs > 0 && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full px-2.5 h-6 text-[10px] font-bold uppercase tracking-widest bg-amber-500/15 text-amber-700 border border-amber-500/30"
                                data-testid={`portal-cert-refs-${c.cert_number}`}
                                title="People who signed up through this certificate's share link"
                              >
                                <Award className="w-3 h-3" /> {refs} ref{refs === 1 ? '' : 's'}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
          </TabsContent>

          {/* ACTIVITY */}
          <TabsContent value="activity">
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2 space-y-6">
              <Card className="pro-card border-0">
                <CardHeader>
                  <CardTitle className="font-heading text-xl font-bold">Your contributions</CardTitle>
                  <CardDescription>What you&apos;ve added to the community.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      { label: 'Forum posts',          value: stats.forum_posts || 0 },
                      { label: 'Recipes shared',       value: stats.recipes_shared || 0 },
                      { label: 'Helpful votes',        value: stats.helpful_votes || 0 },
                      { label: 'Comments',             value: stats.comments_made || 0 },
                      { label: 'Database entries',     value: stats.database_contributions || 0 },
                      { label: `Level ${stats.level || 1}`, value: `${stats.total_points || 0} pts` },
                    ].map(m => (
                      <div key={m.label} className="pro-card p-4">
                        <div className="font-heading text-2xl font-extrabold">{m.value}</div>
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mt-1">{m.label}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="pro-card border-0">
                <CardHeader>
                  <CardTitle className="font-heading text-xl font-bold flex items-center gap-2">
                    <Award className="w-5 h-5 text-primary" /> Badges
                  </CardTitle>
                  <CardDescription>Milestones you&apos;ve earned.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(badges).map(([key, badge]) => {
                      const earned = stats.badges?.includes(key);
                      return (
                        <div
                          key={key}
                          className={`p-4 rounded-2xl border ${
                            earned
                              ? 'border-primary/40 bg-primary/5'
                              : 'border-border bg-muted/40 opacity-70'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="text-3xl">{badge.icon}</div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-heading font-bold text-sm">{badge.name}</h3>
                              <p className="text-xs text-muted-foreground mt-0.5">{badge.description}</p>
                              <div className="mt-2 flex items-center gap-2">
                                <Badge className="bg-primary/10 text-primary hover:bg-primary/10">{badge.points} pts</Badge>
                                {earned && <Badge variant="outline">✓ Earned</Badge>}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              </div>
              {/* Right rail — ActivityFeed */}
              <div className="lg:col-span-1">
                <div className="lg:sticky lg:top-24">
                  <ActivityFeed allowCommunityToggle />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* BILLING */}
          <TabsContent value="billing">
            <div className="grid gap-6">
              <div className="pro-card p-6 sm:p-8 border-0 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <CreditCard className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Current plan</div>
                    <div className="font-heading text-2xl font-extrabold mt-0.5">
                      {(profile.role || 'Free').toUpperCase()} member
                    </div>
                    <div className="text-sm text-muted-foreground mt-0.5">Active subscription.</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button className="rounded-full font-semibold bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate('/pricing')} data-testid="upgrade-btn">
                    Upgrade plan <ArrowUpRight className="ml-1.5 w-3.5 h-3.5" />
                  </Button>
                  <Button variant="outline" className="rounded-full font-semibold" onClick={() => navigate('/pricing')} data-testid="view-plans-btn">
                    View all plans
                  </Button>
                </div>
              </div>

              <Card className="pro-card border-0">
                <CardHeader>
                  <CardTitle className="font-heading text-xl font-bold">Billing history</CardTitle>
                  <CardDescription>Past invoices and payments.</CardDescription>
                </CardHeader>
                <CardContent>
                  <BillingHistory API={API} />
                </CardContent>
              </Card>

              {/* Self-serve refund panel — visible to any user; the panel
                  itself explains eligibility rules and gates the button. */}
              <RefundRequestPanel />
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);

const BillingHistory = ({ API }) => {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/user/orders`).then((r) => {
      if (!alive) return;
      setTxns(r.data?.transactions || []);
    }).catch(() => {}).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [API]);

  if (loading) return <div className="text-center text-sm text-muted-foreground py-8">Loading…</div>;
  if (txns.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground" data-testid="billing-history-empty">
        <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p className="font-heading text-lg font-bold text-foreground">No invoices yet</p>
        <p className="text-xs mt-1">Your Stripe purchases will appear here.</p>
      </div>
    );
  }
  const labelForItem = (it) => {
    if (it.kind === 'subscription') return `${it.tier_display_name || it.tier_name} · ${it.billing_cycle}`;
    if (it.kind === 'lms_course') return `Course · ${it.course_title || 'Untitled'}`;
    if (it.kind === 'event_ticket') return `Ticket · ${it.event_title || 'Event'}`;
    if (it.kind === 'store_product') return `Product · ${it.product_name || 'Item'}`;
    return it.kind;
  };
  const statusBadge = (s) => {
    if (s === 'paid') return 'bg-emerald-500/15 text-emerald-700';
    if (s === 'refunded') return 'bg-rose-500/15 text-rose-700';
    if (s === 'partially_refunded') return 'bg-amber-500/15 text-amber-700';
    return 'bg-muted text-muted-foreground';
  };
  return (
    <div className="space-y-3" data-testid="billing-history-list">
      {txns.map((t) => (
        <div key={t.id || t.session_id} className="rounded-lg border border-border p-3" data-testid={`txn-row-${t.session_id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-heading font-bold truncate">
                {(t.items || []).map(labelForItem).join(' + ') || 'Purchase'}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {new Date(t.created_at).toLocaleString()} · ${((t.amount_cents || 0) / 100).toFixed(2)} {String(t.currency || 'usd').toUpperCase()}
              </div>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${statusBadge(t.payment_status)}`}>
              {t.payment_status || 'pending'}
            </span>
          </div>
          {t.influencer?.code && (
            <div className="text-[11px] mt-1 text-primary/80">
              Promo <strong>{t.influencer.code}</strong> · {t.influencer.discount_pct}% off (-${((t.influencer.discount_cents || 0) / 100).toFixed(2)})
            </div>
          )}
          {(t.refunds || []).length > 0 && (
            <div className="text-[11px] mt-1 text-muted-foreground">
              Refunded ${((t.refunds || []).reduce((s, r) => s + (r.amount_cents || 0), 0) / 100).toFixed(2)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const StudyBuddyOptInToggle = ({ API }) => {
  const [optin, setOptin] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/user/study-buddy-preferences`)
      .then((r) => { if (alive) setOptin(!!r.data?.optin); })
      .catch(() => {});
    return () => { alive = false; };
  }, [API]);
  const toggle = async () => {
    const next = !optin;
    setBusy(true);
    try {
      await axios.put(`${API}/user/study-buddy-preferences`, { optin: next });
      setOptin(next);
      toast.success(next ? 'You’re in — expect a match soon' : 'Opted out of Study Buddy matches');
    } catch { toast.error('Could not save preference'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">Match me with a study partner</div>
        <p className="text-xs text-muted-foreground mt-0.5">
          A nightly matcher pairs learners working on the same lesson in overlapping timezones and DMs both of you a friendly intro. One match per run.
        </p>
      </div>
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        data-testid="study-buddy-optin-toggle"
        aria-pressed={optin}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${optin ? 'bg-primary' : 'bg-muted'}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${optin ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );
};

const ProfileCompletionCard = ({ API }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/user/profile-completion`)
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [API]);

  if (!data) return null;
  const pct = Math.round(data.percentage || 0);
  const isComplete = !!data.is_complete;

  return (
    <Card className="pro-card border-0" data-testid="profile-completion-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-lg font-bold flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-primary" /> Profile completion
        </CardTitle>
        <CardDescription>
          {isComplete
            ? 'Fully unlocked. All member tools are available.'
            : 'Fill these to unlock the Welcome badge — nothing blocks calculators anymore.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Progress</span>
          <span className={`font-heading font-extrabold text-2xl ${isComplete ? 'text-primary' : 'text-foreground'}`}>{pct}%</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${isComplete ? 'bg-primary' : 'bg-primary/70'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {!isComplete && data.missing_fields?.length > 0 && (
          <div className="pt-1">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Still missing</div>
            <ul className="space-y-1">
              {data.missing_fields.map(f => (
                <li key={f} className="text-sm text-foreground/90 flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {f}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserPortal;
